import { useState, useEffect, useCallback } from "react";
import CustomDesignRequestDialog from "./CustomDesignRequestDialog";
import { supabase } from "@lib/supabase/client";
import { Card, CardContent } from "@shared/components/ui/card";
import ClinicImageUpload from "./ClinicImageUpload";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { toast } from "sonner";
import {
  Check, Lock, Eye, Sparkles, Crown, Stethoscope,
  Palette, Layout, Zap, Building2, Heart, Package, Type, Languages, BookOpen,
  Mail, Phone, Users, Award, Star, ShoppingCart, Loader2
} from "lucide-react";
import { motion } from "framer-motion";
import {
  clinicTemplates,
  getTemplatesForPlan,
  isTemplateAvailableForPlan,
  ClinicTemplate
} from "@shared/utils/clinicTemplates";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@shared/components/ui/dialog";

// Available font options for clinic pages
export const clinicFonts = [
  { id: 'ibm-plex', name: 'آي بي إم', nameEn: 'IBM Plex', fontFamily: "'IBM Plex Sans Arabic', sans-serif", googleFont: 'IBM+Plex+Sans+Arabic:wght@400;500;700' },
  { id: 'tajawal', name: 'تجوال', nameEn: 'Tajawal', fontFamily: "'Tajawal', sans-serif", googleFont: 'Tajawal:wght@400;500;700' },
  
  { id: 'almarai', name: 'المراعي', nameEn: 'Almarai', fontFamily: "'Almarai', sans-serif", googleFont: 'Almarai:wght@400;700' },
  { id: 'sakkal', name: 'صقال', nameEn: 'Sakkal Majalla', fontFamily: "'Sakkal Majalla', 'Traditional Arabic', serif", googleFont: null },
  { id: 'times', name: 'تايمز', nameEn: 'Times New Roman', fontFamily: "'Times New Roman', 'Amiri', serif", googleFont: 'Amiri:wght@400;700' },
];

// Available language options for clinic pages
export const clinicLanguages = [
  { id: 'ar', name: 'العربية', nameEn: 'Arabic', direction: 'rtl' as const },
  { id: 'en', name: 'English', nameEn: 'English', direction: 'ltr' as const },
];

interface ClinicTemplateSelectorProps {
  currentTemplateId: string;
  planName: string;
  colors: { primary: string; secondary: string };
  clinicName: string;
  clinicLogo?: string | null;
  onSelectTemplate: (templateId: string) => void;
  selectedFont?: string;
  onSelectFont?: (fontId: string) => void;
  selectedLanguage?: string;
  onSelectLanguage?: (langId: string) => void;
  isAdmin?: boolean;
  isFreeActivated?: boolean;
  coverImageUrl?: string | null;
  onCoverImageChange?: (url: string) => void;
  clinicId?: string;
  specialistName?: string;
  contactEmail?: string;
  contactPhone?: string;
  onPurchaseStatusChange?: (checker: (type: string, key: string) => boolean) => void;
}

const ClinicTemplateSelector = ({
  currentTemplateId,
  planName,
  colors,
  clinicName,
  clinicLogo,
  onSelectTemplate,
  selectedFont = 'ibm-plex',
  onSelectFont,
  selectedLanguage = 'ar',
  onSelectLanguage,
  isAdmin = false,
  isFreeActivated = false,
  coverImageUrl,
  onCoverImageChange,
  clinicId,
  specialistName = '',
  contactEmail = '',
  contactPhone = '',
  onPurchaseStatusChange,
}: ClinicTemplateSelectorProps) => {
  const [previewTemplate, setPreviewTemplate] = useState<ClinicTemplate | null>(null);
  const [showDesignRequest, setShowDesignRequest] = useState(false);
  const [customTemplates, setCustomTemplates] = useState<{ id: string; template_name: string; template_key: string; description: string | null; custom_styles: Record<string, unknown> | null; }[]>([]);
  const [purchases, setPurchases] = useState<{ item_type: string; item_key: string; payment_status: string }[]>([]);
  const [buyingItem, setBuyingItem] = useState<string | null>(null);
  const [designRequests, setDesignRequests] = useState<{ id: string; status: string; price: number; payment_status: string }[]>([]);
  const [buyingDesignRequest, setBuyingDesignRequest] = useState<string | null>(null);
  const [dynamicPrices, setDynamicPrices] = useState<Record<string, number>>({});

  // Product key mapping for customization items
  const PRODUCT_KEY_MAP: Record<string, Record<string, string>> = {
    template: { 'classic-medical': '', 'wellness-warm': 'clinic-template-warm', 'corporate-dark': 'clinic-template-corporate' },
    font: { 'ibm-plex': '', 'tajawal': 'clinic-font-tajawal', 'almarai': 'clinic-font-almarai', 'sakkal': 'clinic-font-sakkal', 'times': 'clinic-font-times' },
    language: { 'ar': '', 'en': 'clinic-lang-en' },
  };

  // Fallback prices (used until DB prices load)
  const FALLBACK_PRICES: Record<string, Record<string, number>> = {
    template: { 'classic-medical': 0, 'wellness-warm': 150, 'corporate-dark': 350 },
    font: { 'ibm-plex': 0, 'tajawal': 25, 'almarai': 25, 'sakkal': 25, 'times': 25 },
    language: { 'ar': 0, 'en': 100 },
  };

  const getItemPrice = (type: string, key: string): number => {
    const productKey = PRODUCT_KEY_MAP[type]?.[key];
    if (!productKey) return 0; // Free item
    if (dynamicPrices[productKey] !== undefined) return dynamicPrices[productKey];
    return FALLBACK_PRICES[type]?.[key] ?? 0;
  };

  const isItemFree = (type: string, key: string): boolean => {
    return getItemPrice(type, key) === 0;
  };

  const isItemPurchased = useCallback((type: string, key: string): boolean => {
    if (isAdmin) return true;
    if (isFreeActivated) return true;
    if (isItemFree(type, key)) return true;
    return purchases.some(p => p.item_type === type && p.item_key === key && p.payment_status === 'paid');
  }, [purchases, isAdmin, isFreeActivated]);

  // Pass purchase checker to parent
  useEffect(() => {
    onPurchaseStatusChange?.(isItemPurchased);
  }, [isItemPurchased, onPurchaseStatusChange]);

  // Fetch dynamic prices from products table
  useEffect(() => {
    const fetchPrices = async () => {
      const productKeys = [
        'clinic-template-warm', 'clinic-template-corporate',
        'clinic-font-tajawal', 'clinic-font-almarai', 'clinic-font-sakkal', 'clinic-font-times',
        'clinic-lang-en'
      ];
      const { data } = await supabase
        .from('products')
        .select('product_key, price')
        .in('product_key', productKeys);
      if (data) {
        const priceMap: Record<string, number> = {};
        data.forEach((p) => { priceMap[p.product_key] = p.price; });
        setDynamicPrices(priceMap);
      }
    };
    fetchPrices();
  }, []);

  // Fetch purchases for this clinic
  useEffect(() => {
    if (!clinicId) return;
    const fetchPurchases = async () => {
      const { data } = await supabase
        .from("clinic_customization_purchases")
        .select("item_type, item_key, payment_status")
        .eq("clinic_id", clinicId)
        .eq("payment_status", "paid");
      if (data) setPurchases(data);
    };
    fetchPurchases();
  }, [clinicId]);

  // Fetch design requests for this clinic
  const fetchDesignRequests = useCallback(async () => {
    if (!clinicId) return;
    const { data } = await supabase
      .from("clinic_custom_design_requests" as never)
      .select("id, status, price, payment_status")
      .eq("clinic_id", clinicId)
      .order("created_at", { ascending: false });
    if (data) setDesignRequests(data as unknown as { id: string; status: string; price: number; payment_status: string }[]);
  }, [clinicId]);

  useEffect(() => {
    fetchDesignRequests();
  }, [fetchDesignRequests]);

  // Fetch custom templates for this clinic
  useEffect(() => {
    if (!clinicId) return;
    const fetchCustomTemplates = async () => {
      const { data } = await supabase
        .from("clinic_custom_templates")
        .select("id, template_name, template_key, description, custom_styles")
        .eq("clinic_id", clinicId)
        .eq("is_active", true);
      if (data) setCustomTemplates(data as unknown as { id: string; template_name: string; template_key: string; description: string; custom_styles: Record<string, unknown> | null; }[]);
    };
    fetchCustomTemplates();
  }, [clinicId]);

  // Handle paying for a design request
  const handleDesignPayment = async (req: { id: string; price: number }) => {
    setBuyingDesignRequest(req.id);
    try {
      const { data: paymentData, error: paymentError } = await supabase.functions.invoke(
        "create-moyasar-payment",
        {
          body: {
            paymentType: "custom_design_request",
            clinicId,
            amount: req.price,
            description: "تصميم مخصص للعيادة الإلكترونية - DietiTech",
            metadata: {
              design_request_id: req.id,
            },
          },
        }
      );

      if (paymentError) throw paymentError;

      if (paymentData?.paymentUrl) {
        window.location.href = paymentData.paymentUrl;
      } else {
        throw new Error("لم يتم الحصول على رابط الدفع");
      }
    } catch (err) {
      console.error("Error creating design payment:", err);
      toast.error("حدث خطأ أثناء إنشاء عملية الدفع");
    } finally {
      setBuyingDesignRequest(null);
    }
  };

  // Handle buying a customization item
  const handleBuyItem = async (type: string, key: string) => {
    const price = getItemPrice(type, key);
    if (price === 0) return;

    const itemId = `${type}-${key}`;
    setBuyingItem(itemId);

    try {
      const itemNames: Record<string, string> = {
        'wellness-warm': 'قالب عصري',
        'corporate-dark': 'قالب مؤسسي',
        'tajawal': 'خط تجوال',
        'almarai': 'خط المراعي',
        'sakkal': 'خط صقال',
        'times': 'خط تايمز',
        'en': 'اللغة الإنجليزية',
      };
      const displayName = itemNames[key] || key;

      const { data, error } = await supabase.functions.invoke('create-moyasar-payment', {
        body: {
          paymentType: 'clinic_customization',
          clinicId,
          amount: price,
          description: `${displayName} - العيادة الإلكترونية`,
          metadata: {
            item_type: type,
            item_key: key,
          },
        },
      });

      if (error) throw error;
      if (data?.paymentUrl) {
        window.location.href = data.paymentUrl;
      }
    } catch (err) {
      console.error("Error creating payment:", err);
      toast.error("حدث خطأ أثناء إنشاء عملية الدفع");
    } finally {
      setBuyingItem(null);
    }
  };
  
  // Admins can access all templates regardless of plan
  const availableTemplates = isAdmin ? clinicTemplates : getTemplatesForPlan(planName);

  // Check if custom design card will be shown
  const normalizedPlanForGrid = planName.toLowerCase();
  const showCustomDesignCard = normalizedPlanForGrid.includes('advanced') || normalizedPlanForGrid.includes('متقدم') || normalizedPlanForGrid.includes('enterprise') || normalizedPlanForGrid.includes('المنشآت') || isAdmin;
  
  // Check template availability - admins can access all
  const checkTemplateAvailable = (templateId: string) => {
    if (isAdmin) return true;
    if (isFreeActivated) return true;
    return isTemplateAvailableForPlan(templateId, planName);
  };

  const getTemplateIcon = (template: ClinicTemplate) => {
    switch (template.id) {
      case 'classic-medical':
        return <Stethoscope className="h-5 w-5" />;
      case 'modern-teal':
        return <Zap className="h-5 w-5" />;
      case 'corporate-dark':
        return <Building2 className="h-5 w-5" />;
      case 'hero-medical':
        return <Crown className="h-5 w-5" />;
      case 'elegant-minimal':
        return <Layout className="h-5 w-5" />;
      case 'wellness-warm':
        return <Heart className="h-5 w-5" />;
      default:
        return <Palette className="h-5 w-5" />;
    }
  };

  // Fixed catalog colors for templates - these never change based on user selection
  // Templates are like a catalog - each has fixed reference colors
  const getTemplateThemeColors = (template: ClinicTemplate) => {
    switch (template.styles.theme) {
      case 'teal':
        return { bg: '#f0fdfa', header: '#14b8a6', accent: '#0d9488' };
      case 'sky':
        return { bg: '#f0f9ff', header: '#b8dbe8', accent: '#7cb9d0' };
      case 'navy':
        return { bg: '#f8fafc', header: '#1e3a5f', accent: '#3b82f6' };
      case 'warm':
        return { bg: '#fffbf5', header: '#e5d5cb', accent: '#d4c0b3' };
      case 'minimal':
        return { bg: '#ffffff', header: '#f5f5f5', accent: '#6b7280' };
      case 'dark':
        return { bg: '#111827', header: '#1f2937', accent: '#b8860b' };
      case 'light':
      default:
        // Classic template - all gray theme
        return { bg: '#ffffff', header: '#9ca3af', accent: '#6b7280' };
    }
  };

  const renderMiniPreview = (template: ClinicTemplate) => {
    const themeColors = getTemplateThemeColors(template);
    const isDark = template.styles.theme === 'dark';
    const isMinimal = template.styles.theme === 'minimal';
    
    return (
      <div 
        className="w-full h-28 rounded-lg overflow-hidden border shadow-sm"
        style={{ backgroundColor: themeColors.bg }}
      >
        {/* Top-bar template mini preview (corporate) */}
        {template.styles.profilePosition === 'top-bar' ? (
          <>
            {/* Mini top navbar */}
            <div className="flex items-center justify-between px-2 py-1 border-b" style={{ borderColor: '#e5e7eb' }}>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: themeColors.accent }} />
                <div className="h-1.5 rounded" style={{ backgroundColor: '#374151', width: '20px' }} />
              </div>
              <div className="flex items-center gap-0.5">
                <div className="h-2 px-1 rounded text-[4px]" style={{ backgroundColor: themeColors.accent }} />
                <div className="w-2 h-2 rounded border" style={{ borderColor: '#d1d5db' }} />
              </div>
            </div>
            {/* Hero section */}
            <div 
              className="h-12 relative"
              style={{ backgroundColor: themeColors.header }}
            >
              <div className="absolute inset-0" style={{ backgroundColor: `${themeColors.accent}60` }} />
            </div>
            {/* Product cards */}
            <div className="px-2 mt-1.5 flex gap-1">
              {[1, 2, 3].map(i => (
                <div 
                  key={i}
                  className="flex-1 h-6 rounded"
                  style={{ 
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Mini header - varies by template */}
            <div 
              className={`relative ${
                template.styles.headerHeight === 'hero' ? 'h-14' :
                template.styles.headerHeight === 'tall' ? 'h-12' : 
                template.styles.headerHeight === 'short' ? 'h-6' : 'h-8'
              }`}
              style={{ backgroundColor: isMinimal ? '#f5f5f5' : themeColors.header }}
            >
              {/* Header decorations based on style */}
              {template.styles.headerStyle === 'wave' && (
                <div className="absolute bottom-0 left-0 right-0 h-2 bg-white" style={{ borderRadius: '100% 100% 0 0' }} />
              )}
              {template.styles.headerStyle === 'split' && (
                <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-white/20" />
              )}
              {template.styles.decorations === 'organic-shapes' && (
                <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-white/20" />
              )}
            </div>

            {/* Profile preview based on position */}
            {template.styles.profilePosition === 'floating' && (
              <div className="px-2 -mt-3">
                <div 
                  className="bg-white rounded-md shadow-md p-1.5 flex items-center gap-1.5"
                  style={{ 
                    border: isMinimal ? '1px solid #e5e7eb' : 'none'
                  }}
                  dir="ltr"
                >
                  <div 
                    className="h-3 px-1.5 rounded text-[6px] flex items-center shrink-0"
                    style={{ backgroundColor: isMinimal ? '#6b7280' : themeColors.accent }}
                  />
                  <div className="flex-1" />
                  <div className="space-y-0.5">
                    <div 
                      className="h-1.5 rounded ml-auto"
                      style={{ backgroundColor: isDark ? '#4b5563' : '#374151', width: '24px' }}
                    />
                    <div 
                      className="h-1 rounded ml-auto"
                      style={{ backgroundColor: isDark ? '#374151' : '#9ca3af', width: '16px' }}
                    />
                  </div>
                  <div 
                    className="w-5 h-5 rounded-full shrink-0 border-2 border-white shadow-sm"
                    style={{ backgroundColor: isDark ? '#4b5563' : isMinimal ? '#9ca3af' : themeColors.accent }}
                  />
                </div>
              </div>
            )}
            
            {template.styles.profilePosition !== 'floating' && (
              <div className="px-2 mt-1">
                <div className="flex items-start gap-2">
                  <div 
                    className="w-6 h-6 rounded-full shrink-0"
                    style={{ backgroundColor: themeColors.accent }}
                  />
                  <div className="flex-1 space-y-1 pt-1">
                    <div 
                      className="h-1.5 rounded"
                      style={{ backgroundColor: isDark ? '#4b5563' : '#d1d5db', width: '50%' }}
                    />
                    <div 
                      className="h-1 rounded"
                      style={{ backgroundColor: isDark ? '#374151' : '#e5e7eb', width: '70%' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Product cards preview */}
            <div 
              className={`px-2 mt-2 flex gap-1 ${template.styles.productsLayout === 'list' ? 'flex-col' : ''}`}
              dir={template.styles.productsLayout === 'list' ? 'ltr' : undefined}
            >
              {(template.styles.productsLayout === 'list' ? [1, 2] : [1, 2, 3]).map(i => (
                <div 
                  key={i}
                  className={`${template.styles.productsLayout === 'list' ? 'h-2.5 flex items-center gap-1' : 'flex-1 h-6'}`}
                  style={{ 
                    backgroundColor: isDark ? '#374151' : 
                      template.styles.cardStyle === 'glass' ? 'rgba(255,255,255,0.6)' :
                      template.styles.cardStyle === 'floating' || template.styles.cardStyle === 'elevated' ? '#ffffff' : '#f9fafb',
                    borderRadius: template.styles.cardRadius === 'full' ? '8px' : 
                      template.styles.cardRadius === 'large' ? '6px' : '4px',
                    border: (template.styles.cardStyle === 'bordered' || template.styles.cardStyle === 'elevated') ? '1px solid #e5e7eb' : 'none',
                    boxShadow: template.styles.cardStyle === 'elevated' || template.styles.cardStyle === 'floating' 
                      ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  {template.styles.productsLayout === 'list' && (
                    <>
                      <div className="w-3 h-2 rounded shrink-0" style={{ backgroundColor: isMinimal ? '#6b7280' : themeColors.accent }} />
                      <div className="flex-1" />
                      <div className="h-1 rounded" style={{ backgroundColor: '#d1d5db', width: '20px' }} />
                      <div className="w-2.5 h-2 rounded shrink-0" style={{ backgroundColor: isMinimal ? '#e5e7eb' : `${themeColors.accent}30` }} />
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // Build Google Fonts URL (filter out null googleFont values)
  const googleFontsUrl = clinicFonts
    .filter(f => f.googleFont)
    .map(f => f.googleFont)
    .join('&family=');

  return (
    <div className="space-y-4" dir="rtl">
      {/* Load all clinic fonts for preview */}
      <link 
        href={`https://fonts.googleapis.com/css2?family=${googleFontsUrl}&display=swap`} 
        rel="stylesheet" 
      />
      {/* Header */}
      <div className="flex items-center justify-between flex-row-reverse">
        <Badge variant="outline" className="gap-1">
          {availableTemplates.length} قالب متاح
        </Badge>
        <div className="text-right">
        </div>
      </div>

      {/* Templates Grid */}
      <div className={`grid grid-cols-2 ${showCustomDesignCard ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`} style={{ direction: 'rtl' }}>
        {clinicTemplates.map((template) => {
          const isAvailable = checkTemplateAvailable(template.id);
          const isSelected = currentTemplateId === template.id;

          return (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card 
                className={`relative cursor-pointer transition-all overflow-hidden ${
                  isSelected 
                    ? 'ring-2 ring-primary border-primary' 
                    : isAvailable 
                      ? 'hover:border-primary/50 hover:shadow-md' 
                      : 'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => isAvailable && onSelectTemplate(template.id)}
              >
                {/* Selected indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                )}

                {/* Locked indicator */}
                {!isAvailable && (
                  <div className="absolute inset-0 bg-background/80 z-10 flex flex-col items-center justify-center gap-2">
                    <Lock className="h-6 w-6 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      متاح في الباقة المتقدمة
                    </span>
                  </div>
                )}

                <CardContent className="p-3 space-y-3">
                  {/* Mini Preview */}
                  {renderMiniPreview(template)}

                  {/* Template Info */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: 'hsl(var(--muted))' }}
                        >
                          {getTemplateIcon(template)}
                        </div>
                        <span className="font-medium text-sm">{template.nameAr}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {template.description}
                    </p>
                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-1">
                        {isItemFree('template', template.id) ? (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">مجاني</Badge>
                        ) : isItemPurchased('template', template.id) ? (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">مُفعّل</Badge>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewTemplate(template);
                          }}
                        >
                          <Eye className="h-3 w-3" />
                          معاينة
                        </Button>
                        {!isItemFree('template', template.id) && !isItemPurchased('template', template.id) && (
                          <Button
                            variant="default"
                            size="sm"
                            className="h-7 text-xs gap-1 flex-row-reverse"
                            disabled={buyingItem === `template-${template.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBuyItem('template', template.id);
                            }}
                          >
                            {buyingItem === `template-${template.id}` ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <ShoppingCart className="h-3 w-3" />
                            )}
                            {getItemPrice('template', template.id)} ر.س
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}

        {/* Custom Design Card - only shown for Advanced and Enterprise plans */}
        {(() => {
          const normalizedPlan = planName.toLowerCase();
          const isAdvancedOrHigher = normalizedPlan.includes('advanced') || normalizedPlan.includes('متقدم') || normalizedPlan.includes('enterprise') || normalizedPlan.includes('المنشآت') || isAdmin;
          if (!isAdvancedOrHigher) return null;

          const activeRequest = designRequests.find(r => r.payment_status === 'awaiting_payment' && r.price > 0);
          const paidRequest = designRequests.find(r => r.payment_status === 'paid');
          const pendingRequest = designRequests.find(r => r.payment_status === 'unpaid' || (r.payment_status !== 'paid' && r.payment_status !== 'awaiting_payment'));
          const displayRequest = paidRequest || activeRequest || pendingRequest;

          return (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card className="relative cursor-pointer transition-all overflow-hidden hover:border-primary/50 hover:shadow-md">
                <CardContent className="p-3 space-y-3">
                  {/* Custom design preview */}
                  <div className="w-full h-28 rounded-lg overflow-hidden border shadow-sm bg-white flex items-center justify-center">
                    <div className="text-center">
                      <Sparkles className="h-8 w-8 text-primary mx-auto mb-1" />
                      <p className="text-[10px] font-medium text-primary">حسب طلبك</p>
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${colors.primary}20` }}
                      >
                        <Crown className="h-5 w-5 text-primary" />
                      </div>
                      <span className="font-medium text-sm">تصميم خاص</span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      تصميم مخصص ل{clinicName}
                    </p>

                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-1">
                        {paidRequest ? (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">مدفوع</Badge>
                        ) : activeRequest ? (
                          <Badge variant="outline" className="text-xs">{activeRequest.price} ر.س</Badge>
                        ) : pendingRequest ? (
                          <Badge variant="outline" className="text-xs">بانتظار التسعير</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">سعر مخصص</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {activeRequest && !paidRequest ? (
                          <Button
                            variant="default"
                            size="sm"
                            className="h-7 text-xs gap-1 flex-row-reverse"
                            disabled={buyingDesignRequest === activeRequest.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDesignPayment(activeRequest);
                            }}
                          >
                            {buyingDesignRequest === activeRequest.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <ShoppingCart className="h-3 w-3" />
                            )}
                            ادفع
                          </Button>
                        ) : !displayRequest ? (
                          <Button
                            variant="default"
                            size="sm"
                            className="h-7 text-xs gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDesignRequest(true);
                            }}
                          >
                            اطلب
                          </Button>
                        ) : null}
                      </div>
                    </div>

                    {/* Status & delivery note */}
                    {displayRequest && (
                      <div className="pt-1 space-y-1">
                        {pendingRequest && !activeRequest && !paidRequest && (
                          <p className="text-[10px] text-muted-foreground">⏳ قيد المراجعة - سيتم تحديد السعر قريباً</p>
                        )}
                        {(activeRequest || paidRequest) && (
                          <p className="text-[10px] text-muted-foreground">⏱️ التنفيذ يستغرق من 24 إلى 48 ساعة</p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })()}
      </div>

      {/* Custom Templates - Only shown if specialist has custom templates */}
      {customTemplates.length > 0 && (
        <div className="space-y-3">
          <div className="text-right">
            <h3 className="font-bold flex items-center gap-2 justify-end flex-row-reverse">
              <Sparkles className="h-5 w-5 text-primary" />
              قالبك المخصص
            </h3>
            <p className="text-sm text-muted-foreground">
              قالب مصمم خصيصاً لعيادتك
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" style={{ direction: 'rtl' }}>
            {customTemplates.map((ct) => {
              const isSelected = currentTemplateId === ct.template_key;
              return (
                <motion.div
                  key={ct.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card
                    className={`relative cursor-pointer transition-all overflow-hidden ${
                      isSelected
                        ? 'ring-2 ring-primary border-primary'
                        : 'hover:border-primary/50 hover:shadow-md'
                    }`}
                    onClick={() => onSelectTemplate(ct.template_key)}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                    <CardContent className="p-3 space-y-3">
                      {/* Custom template preview */}
                      <div className="w-full h-28 rounded-lg overflow-hidden border shadow-sm bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                        <div className="text-center">
                          <Crown className="h-8 w-8 text-primary mx-auto mb-1" />
                          <p className="text-xs font-medium text-primary">تصميم حصري</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                            <Crown className="h-5 w-5 text-primary" />
                          </div>
                          <span className="font-medium text-sm">{ct.template_name}</span>
                        </div>
                        {ct.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2">{ct.description}</p>
                        )}
                        <Badge className="text-xs bg-primary/10 text-primary border-primary/20">مخصص لك</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Old custom design card removed - now integrated in templates grid */}

      {/* Custom Design Request Dialog */}
      {clinicId && (
        <CustomDesignRequestDialog
          open={showDesignRequest}
          onOpenChange={(open) => {
            setShowDesignRequest(open);
            if (!open) fetchDesignRequests();
          }}
          clinicId={clinicId}
          specialistName={specialistName}
          clinicName={clinicName}
          email={contactEmail}
          phone={contactPhone}
        />
      )}

      {/* Font Selection */}
      {onSelectFont && (
        <div className="space-y-4 mt-8 border-t pt-6" dir="rtl">
          <div className="text-right w-full">
            <h3 className="font-bold flex items-center gap-2">
              <Type className="h-5 w-5 text-primary" />
              خط العيادة
            </h3>
          </div>
          
          <div className="grid grid-cols-5 gap-3" style={{ direction: 'rtl' }}>
            {clinicFonts.map((font) => (
              <Card
                key={font.id}
                className={`cursor-pointer transition-all overflow-hidden ${
                  selectedFont === font.id
                    ? 'ring-2 ring-primary border-primary'
                    : 'hover:border-primary/50 hover:shadow-md'
                }`}
                onClick={() => onSelectFont(font.id)}
              >
                <CardContent className="p-3 text-center">
                  {/* Font Preview */}
                  <div 
                    className="mb-3 p-4 rounded-lg bg-muted/50 border"
                    style={{ fontFamily: font.fontFamily }}
                  >
                    <p className="text-2xl font-bold mb-1">عيادتك</p>
                    <p className="text-sm text-muted-foreground">أهلاً بك في عيادتنا</p>
                  </div>
                  
                  {/* Font Name + Price */}
                  <div className="flex items-center justify-between w-full" dir="rtl">
                    <span className="font-medium text-sm">{font.name}</span>
                    <span className="text-xs text-muted-foreground">{font.nameEn}</span>
                  </div>
                  
                  {/* Price / Status */}
                  <div className="mt-2 flex flex-col items-center gap-1">
                    {selectedFont === font.id ? (
                      <Badge className="gap-1 bg-primary text-white">
                        <Check className="h-3 w-3" />
                        مُختار
                      </Badge>
                    ) : isItemFree('font', font.id) ? (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">مجاني</Badge>
                    ) : isItemPurchased('font', font.id) ? (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">مُفعّل</Badge>
                    ) : (
                      <Button
                        variant="default"
                        size="sm"
                        className="h-7 text-xs gap-1 flex-row-reverse"
                        disabled={buyingItem === `font-${font.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBuyItem('font', font.id);
                        }}
                      >
                        {buyingItem === `font-${font.id}` ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <ShoppingCart className="h-3 w-3" />
                        )}
                        {getItemPrice('font', font.id)} ر.س
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Language Selection */}
      {onSelectLanguage && (
        <div className="space-y-4 mt-8 border-t pt-6" dir="rtl">
          <div className="text-right w-full">
            <h3 className="font-bold flex items-center gap-2">
              <Languages className="h-5 w-5 text-primary" />
              لغة العيادة
            </h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4" style={{ direction: 'rtl' }}>
            {clinicLanguages.map((lang) => (
              <Card
                key={lang.id}
                className={`cursor-pointer transition-all overflow-hidden ${
                  selectedLanguage === lang.id
                    ? 'ring-2 ring-primary border-primary'
                    : 'hover:border-primary/50 hover:shadow-md'
                }`}
                onClick={() => onSelectLanguage(lang.id)}
              >
                <CardContent className="p-4 text-center">
                  {/* Language Preview */}
                  <div 
                    className="mb-3 p-4 rounded-lg bg-muted/50 border"
                    dir={lang.direction}
                  >
                    <p className="text-lg font-bold mb-1">
                      {lang.id === 'ar' ? 'مرحباً بك' : 'Welcome'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {lang.id === 'ar' ? 'عيادتك الإلكترونية' : 'Your Electronic Clinic'}
                    </p>
                  </div>
                  
                  {/* Direction indicator */}
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-xs px-2 py-1 rounded bg-muted">
                      {lang.direction === 'rtl' ? '← RTL' : 'LTR →'}
                    </span>
                  </div>
                  
                  {/* Language Name */}
                  <div className="flex items-center justify-center gap-2">
                    <span className="font-medium">{lang.name}</span>
                  </div>
                  
                  {/* Price / Status */}
                  <div className="mt-2 flex flex-col items-center gap-1">
                    {selectedLanguage === lang.id ? (
                      <Badge className="gap-1 bg-primary text-white">
                        <Check className="h-3 w-3" />
                        مُختار
                      </Badge>
                    ) : isItemFree('language', lang.id) ? (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">مجاني</Badge>
                    ) : isItemPurchased('language', lang.id) ? (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">مُفعّل</Badge>
                    ) : (
                      <Button
                        variant="default"
                        size="sm"
                        className="h-7 text-xs gap-1 flex-row-reverse"
                        disabled={buyingItem === `language-${lang.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBuyItem('language', lang.id);
                        }}
                      >
                        {buyingItem === `language-${lang.id}` ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <ShoppingCart className="h-3 w-3" />
                        )}
                        {getItemPrice('language', lang.id)} ر.س
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              معاينة قالب: {previewTemplate?.nameAr}
            </DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <TemplateFullPreview 
              template={previewTemplate} 
              colors={colors}
              clinicName={clinicName}
              clinicLogo={clinicLogo}
              coverImageUrl={coverImageUrl}
            />
          )}
          <div className="flex items-center justify-between gap-2 pt-4 border-t" dir="rtl">
            {/* Header image upload for templates that support it */}
            {previewTemplate && previewTemplate.styles.headerStyle === 'full-image' && onCoverImageChange && clinicId ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground whitespace-nowrap">صورة الهيدر:</span>
                <ClinicImageUpload
                  currentImageUrl={coverImageUrl}
                  onUploadComplete={onCoverImageChange}
                  folder={`clinics/${clinicId}/cover`}
                  aspectRatio="wide"
                  placeholder="رفع صورة"
                  className="!space-y-0"
                />
              </div>
            ) : <div />}
            <div className="flex items-center gap-2">
              <Button onClick={() => {
                if (previewTemplate) {
                  onSelectTemplate(previewTemplate.id);
                  setPreviewTemplate(null);
                }
              }}>
                اختيار هذا القالب
              </Button>
              <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
                إغلاق
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Full Template Preview Component
export const TemplateFullPreview = ({
  template,
  colors: _colors,
  clinicName,
  clinicLogo,
  coverImageUrl
}: {
  template: ClinicTemplate; 
  colors: { primary: string; secondary: string }; 
  clinicName: string;
  clinicLogo?: string | null;
  coverImageUrl?: string | null;
}) => {
  // Fixed catalog colors - templates always show their reference colors, not user's colors
  const getThemeColors = () => {
    switch (template.styles.theme) {
      case 'teal':
        return { bg: '#f0fdfa', header: '#14b8a6', accent: '#0d9488', text: '#0f766e', cardBg: '#ffffff' };
      case 'sky':
        return { bg: '#f0f9ff', header: '#b8dbe8', accent: '#7cb9d0', text: '#3b7a91', cardBg: '#ffffff' };
      case 'navy':
        return { bg: '#ffffff', header: '#1e3a5f', accent: '#1e3a5f', text: '#1f2937', cardBg: '#f8fafc' };
      case 'warm':
        return { bg: '#fffbf5', header: '#e5d5cb', accent: '#d4c0b3', text: '#8a7060', cardBg: '#ffffff' };
      case 'minimal':
        return { bg: '#ffffff', header: '#f5f5f5', accent: '#6b7280', text: '#111827', cardBg: '#ffffff' };
      case 'dark':
        return { bg: '#111827', header: '#1f2937', accent: '#b8860b', text: '#f9fafb', cardBg: '#1f2937' };
      case 'light':
      default:
        // Classic template - all gray theme
        return { bg: '#ffffff', header: '#9ca3af', accent: '#6b7280', text: '#1f2937', cardBg: '#ffffff' };
    }
  };

  const themeColors = getThemeColors();
  const isDark = template.styles.theme === 'dark';

  // Different header heights - matching actual ClinicPublicPage sizes
  const headerHeight = 
    template.styles.headerHeight === 'hero' ? 'h-72' :
    template.styles.headerHeight === 'tall' ? 'h-52' : 
    template.styles.headerHeight === 'short' ? 'h-28' : 'h-36';

  // Profile offset based on header height - to keep profile at consistent position
  const profileOffset = 
    template.styles.headerHeight === 'hero' ? '-mt-44' :
    template.styles.headerHeight === 'tall' ? '-mt-30' : 
    template.styles.headerHeight === 'short' ? '-mt-10' : '-mt-16';

  // Card styles based on template
  const getCardStyle = () => {
    const baseStyle: React.CSSProperties = {
      borderRadius: template.styles.cardRadius === 'full' ? '16px' : 
        template.styles.cardRadius === 'large' ? '12px' : 
        template.styles.cardRadius === 'small' ? '6px' : 
        template.styles.cardRadius === 'none' ? '0' : '8px',
    };

    switch (template.styles.cardStyle) {
      case 'elevated':
        return { ...baseStyle, backgroundColor: themeColors.cardBg, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: 'none' };
      case 'flat':
        return { ...baseStyle, backgroundColor: isDark ? '#374151' : '#f9fafb', boxShadow: 'none', border: 'none' };
      case 'bordered':
        return { ...baseStyle, backgroundColor: themeColors.cardBg, boxShadow: 'none', border: `2px solid ${isDark ? '#4b5563' : '#e5e7eb'}` };
      case 'glass':
        return { ...baseStyle, backgroundColor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid rgba(255,255,255,0.3)' };
      case 'floating':
        return { ...baseStyle, backgroundColor: '#ffffff', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: 'none' };
      case 'minimal':
        return { ...baseStyle, backgroundColor: 'transparent', boxShadow: 'none', borderBottom: `1px solid ${isDark ? '#374151' : '#e5e7eb'}` };
      default:
        return { ...baseStyle, backgroundColor: themeColors.cardBg, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' };
    }
  };

  return (
    <div 
      className="rounded-xl overflow-hidden border"
      style={{ backgroundColor: themeColors.bg }}
      dir="rtl"
    >
      {/* Top navbar for corporate/top-bar template */}
      {template.styles.profilePosition === 'top-bar' && (
        <div className="flex items-center justify-between px-6 py-3 border-b" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex items-center gap-3">
            {clinicLogo ? (
              <img src={clinicLogo} alt="شعار العيادة" className="w-10 h-10 rounded-lg object-contain" />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold"
                style={{ backgroundColor: themeColors.accent }}
              >
                {clinicName?.charAt(0) || 'ع'}
              </div>
            )}
            <div>
              <h2 className="font-bold text-base" style={{ color: themeColors.text }}>{clinicName || 'اسم العيادة'}</h2>
              <p className="text-xs opacity-60" style={{ color: themeColors.text }}>ممارس صحي</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              className="px-3 py-1.5 rounded-lg text-white text-xs font-medium"
              style={{ backgroundColor: themeColors.accent }}
            >
              بوابة المراجع
            </button>
            <div className="flex gap-1">
              <div className="w-7 h-7 rounded-md flex items-center justify-center border" style={{ borderColor: '#d1d5db' }}>
                <Phone className="w-3 h-3" style={{ color: themeColors.accent }} />
              </div>
              <div className="w-7 h-7 rounded-md flex items-center justify-center border" style={{ borderColor: '#d1d5db' }}>
                <Mail className="w-3 h-3" style={{ color: themeColors.accent }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header - Different styles per template */}
      <div 
        className={`${template.styles.profilePosition === 'top-bar' ? 'h-52' : headerHeight} relative overflow-hidden`}
        style={{ 
          backgroundColor: themeColors.header,
          ...((template.styles.headerStyle === 'full-image') ? {
            backgroundImage: `url(${coverImageUrl || '/clinic-defaults/corporate-header.jpg?v=2'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          } : {})
        }}
      >
        {/* Color overlay for top-bar template */}
        {template.styles.profilePosition === 'top-bar' && (
          <div className="absolute inset-0" style={{ backgroundColor: `${themeColors.accent}60` }} />
        )}
        
        {/* Header overlay based on style */}
        {template.styles.headerOverlay === 'gradient' && template.styles.profilePosition !== 'top-bar' && (
          <div 
            className="absolute inset-0" 
            style={{ 
              background: `linear-gradient(to bottom, transparent 0%, transparent 50%, ${themeColors.bg} 100%)` 
            }} 
          />
        )}
        {template.styles.headerOverlay === 'dark' && template.styles.profilePosition !== 'top-bar' && (
          <div className="absolute inset-0 bg-black/40" />
        )}
        
        {/* Bottom gradient for top-bar */}
        {template.styles.profilePosition === 'top-bar' && (
          <div 
            className="absolute bottom-0 left-0 right-0 h-16" 
            style={{ background: `linear-gradient(to top, ${themeColors.bg} 0%, transparent 100%)` }} 
          />
        )}
        
        {/* Wave decoration for warm/wellness */}
        {template.styles.headerStyle === 'wave' && (
          <div className="absolute bottom-0 left-0 right-0 h-10">
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-full">
              <path d="M0,60 C300,100 600,20 900,60 C1050,80 1150,40 1200,60 L1200,120 L0,120 Z" 
                fill={themeColors.bg} />
            </svg>
          </div>
        )}
        
        {/* Split style for teal */}
        {template.styles.headerStyle === 'split' && (
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-white/10" />
        )}
        
        {/* Decorations */}
        {template.styles.decorations === 'organic-shapes' && (
          <>
            <div className="absolute top-4 right-4 w-20 h-20 rounded-full bg-white/15" />
            <div className="absolute bottom-8 left-8 w-32 h-32 rounded-full bg-white/8" />
            <div className="absolute top-1/2 left-1/3 w-12 h-12 rounded-full bg-white/10" />
          </>
        )}
        {template.styles.decorations === 'medical-icons' && (
          <div className="absolute top-4 left-4 opacity-30">
            <Stethoscope className="w-14 h-14 text-white" />
          </div>
        )}
        {template.styles.decorations === 'waves' && (
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1200 60" preserveAspectRatio="none" className="w-full h-8">
              <path d="M0,30 Q300,60 600,30 T1200,30 L1200,60 L0,60 Z" fill={themeColors.bg} />
            </svg>
          </div>
        )}
        
        {/* Hero overlay profile */}
        {template.styles.profilePosition === 'hero-overlay' && (
          <div className="absolute bottom-6 right-6 left-6 flex items-end gap-4">
            {clinicLogo ? (
              <img 
                src={clinicLogo} 
                alt="شعار العيادة" 
                className={`w-20 h-20 ${template.styles.profileStyle === 'circular' ? 'rounded-full' : 'rounded-xl'} border-4 border-white shadow-2xl object-contain bg-white p-1`}
              />
            ) : (
              <div 
                className={`w-20 h-20 ${template.styles.profileStyle === 'circular' ? 'rounded-full' : 'rounded-xl'} border-4 border-white shadow-2xl flex items-center justify-center text-white text-2xl font-bold`}
                style={{ backgroundColor: themeColors.accent }}
              >
                {clinicName?.charAt(0) || 'ع'}
              </div>
            )}
            <div className="text-white pb-2">
              <h2 className="text-xl font-bold drop-shadow-lg">{clinicName || 'اسم العيادة'}</h2>
              <p className="text-sm text-white/80">ممارس صحي</p>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className={`p-6 ${template.styles.layout === 'wide' ? 'max-w-5xl' : template.styles.layout === 'compact' ? 'max-w-2xl' : 'max-w-4xl'} mx-auto`}>
        {/* Floating profile - LTR forced for correct visual order */}
        {template.styles.profilePosition === 'floating' && (
          <div className={`${profileOffset} mb-6 relative z-10`}>
            <div 
              className="flex items-center gap-4 p-4 rounded-xl bg-white shadow-lg"
              style={{
                ...getCardStyle(),
                border: template.styles.theme === 'minimal' ? '1px solid #e5e7eb' : 'none'
              }}
              dir="ltr"
            >
              {/* Button - on LEFT side of screen */}
              <button 
                className="px-4 py-2 rounded-lg text-white text-sm font-medium shrink-0"
                style={{ backgroundColor: template.styles.theme === 'minimal' ? '#6b7280' : themeColors.accent }}
              >
                بوابة المراجعين
              </button>
              
              {/* Spacer to push content to the right */}
              <div className="flex-1" />
              
              {/* Text content - next to logo on RIGHT - use theme colors */}
              <div className="text-right" style={{ color: themeColors.text }}>
                <div className="h-4 rounded mb-2 ml-auto" style={{ backgroundColor: themeColors.accent, width: '120px' }} />
                <div className="h-3 rounded ml-auto" style={{ backgroundColor: `${themeColors.accent}60`, width: '80px' }} />
              </div>
              
              {/* Logo/Avatar - on RIGHT side of screen */}
              {clinicLogo ? (
                <img src={clinicLogo} alt="شعار العيادة" className="w-14 h-14 rounded-full object-contain bg-white border-2 border-white shadow-md shrink-0" />
              ) : (
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-md shrink-0"
                  style={{ backgroundColor: template.styles.theme === 'minimal' ? '#9ca3af' : themeColors.accent }}
                >
                  {clinicName?.charAt(0) || 'ع'}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Inline profile */}
        {template.styles.profilePosition === 'inline' && (
          <div className="mb-6">
            <div className="flex flex-row items-center gap-4">
              {clinicLogo ? (
                <img src={clinicLogo} alt="شعار العيادة" className="w-14 h-14 rounded-xl object-contain bg-white shadow-md p-1" />
              ) : (
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-md"
                  style={{ backgroundColor: themeColors.accent }}
                >
                  {clinicName?.charAt(0) || 'ع'}
                </div>
              )}
              <div style={{ color: themeColors.text }}>
                <h2 className="text-xl font-bold">{clinicName || 'اسم العيادة'}</h2>
                <p className="text-sm opacity-60">ممارس صحي</p>
              </div>
            </div>
          </div>
        )}

        {/* Sidebar profile - for asymmetric layout (wellness-warm) */}
        {template.styles.profilePosition === 'sidebar' && (
          <div className={`${profileOffset} mb-6 flex gap-6 relative z-10`}>
            {/* Sidebar with profile */}
            <div 
              className="w-1/3 p-4 rounded-xl"
              style={{ 
                ...getCardStyle(),
                backgroundColor: `${themeColors.accent}10`
              }}
            >
              <div className="text-center">
                {clinicLogo ? (
                  <img src={clinicLogo} alt="شعار العيادة" className="w-16 h-16 rounded-full mx-auto object-contain bg-white shadow-lg p-1 mb-3" />
                ) : (
                  <div 
                    className="w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white text-xl font-bold shadow-lg mb-3"
                    style={{ backgroundColor: themeColors.accent }}
                  >
                    {clinicName?.charAt(0) || 'ع'}
                  </div>
                )}
                <h3 className="font-bold text-sm mb-1" style={{ color: themeColors.text }}>{clinicName || 'اسم العيادة'}</h3>
                <p className="text-xs opacity-60 mb-3" style={{ color: themeColors.text }}>ممارس صحي</p>
                {/* Contact icons instead of button */}
                <div className="flex justify-center gap-2">
                  <div 
                    className="w-9 h-9 rounded-lg flex items-center justify-center border"
                    style={{ borderColor: `${themeColors.accent}30` }}
                  >
                    <Mail className="w-4 h-4" style={{ color: themeColors.accent }} />
                  </div>
                  <div 
                    className="w-9 h-9 rounded-lg flex items-center justify-center border"
                    style={{ borderColor: `${themeColors.accent}30` }}
                  >
                    <Phone className="w-4 h-4" style={{ color: themeColors.accent }} />
                  </div>
                </div>
              </div>
            </div>
            {/* Main content area - About & Stats */}
            <div className="flex-1 space-y-4">
              {/* About Section */}
              <div 
                className="p-4 rounded-xl"
                style={{ 
                  backgroundColor: isDark ? '#1f2937' : '#ffffff',
                  border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`
                }}
              >
                <h4 className="font-bold text-sm mb-2 flex items-center gap-2" style={{ color: themeColors.text }}>
                  <BookOpen className="w-4 h-4" style={{ color: themeColors.accent }} />
                  نبذة عنّا
                </h4>
                <p className="text-xs leading-relaxed opacity-70" style={{ color: themeColors.text }}>
                  نقدم خدمات التغذية العلاجية المتميزة لمساعدتك في تحقيق أهدافك الصحية وبناء نمط حياة صحي ومستدام.
                </p>
              </div>
              
              {/* Stats Section */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Users, value: '+500', label: 'عميل' },
                  { icon: Award, value: '+10', label: 'سنوات خبرة' },
                  { icon: Star, value: '5/5', label: 'تقييم' }
                ].map((stat, i) => (
                  <div 
                    key={i}
                    className="text-center p-3 rounded-lg"
                    style={{ 
                      backgroundColor: `${themeColors.accent}10`,
                      border: `1px solid ${themeColors.accent}20`
                    }}
                  >
                    <stat.icon className="w-4 h-4 mx-auto mb-1" style={{ color: themeColors.accent }} />
                    <div className="font-bold text-sm" style={{ color: themeColors.accent }}>{stat.value}</div>
                    <div className="text-xs opacity-60" style={{ color: themeColors.text }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Stats section for teal theme */}
        {template.styles.effects.includes('stats-counter') && (
          <div className="flex justify-around py-4 mb-6 border-b" style={{ borderColor: '#e5e7eb' }}>
            {[{ val: '+500', label: 'مراجع' }, { val: '+10', label: 'سنوات خبرة' }, { val: '٥/٥', label: 'تقييم' }].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-bold" style={{ color: themeColors.accent }}>{stat.val}</div>
                <div className="text-xs opacity-60" style={{ color: themeColors.text }}>{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* About Section for non-sidebar templates */}
        {template.styles.profilePosition !== 'sidebar' && (
          <div className="mb-6 p-4 rounded-xl" style={{ ...getCardStyle() }}>
            <h4 className="font-bold text-sm mb-2 flex items-center gap-2" style={{ color: themeColors.text }}>
              <BookOpen className="w-4 h-4" style={{ color: themeColors.accent }} />
              نبذة عنّا
            </h4>
            <p className="text-xs leading-relaxed opacity-70" style={{ color: themeColors.text }}>
              نقدم خدمات التغذية العلاجية المتميزة لمساعدتك في تحقيق أهدافك الصحية وبناء نمط حياة صحي ومستدام.
            </p>
            <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${isDark ? '#374151' : '#e5e7eb'}` }}>
              <h4 className="font-bold text-sm mb-2 flex items-center gap-2" style={{ color: themeColors.text }}>
                <Award className="w-4 h-4" style={{ color: themeColors.accent }} />
                عن المختص
              </h4>
              <p className="text-xs leading-relaxed opacity-70" style={{ color: themeColors.text }}>
                خبرة في مجال التغذية العلاجية وإدارة الوزن والأمراض المزمنة.
              </p>
            </div>
          </div>
        )}

        {/* Products Section */}
        <h3 className="font-bold mb-4 text-center" style={{ color: themeColors.text }}>الباقات المتاحة</h3>
        
        {/* Products - Force LTR so: Button LEFT, Lines MIDDLE, Image RIGHT on screen */}
        {template.styles.productsLayout === 'list' ? (
          <div className="space-y-3" dir="ltr">
            {[1, 2, 3].map(i => (
              <div 
                key={i} 
                className="flex items-center gap-3 p-3" 
                style={{
                  ...getCardStyle(),
                  border: template.styles.theme === 'minimal' ? '1px solid #e5e7eb' : undefined
                }}
              >
                {/* Button on LEFT side of screen */}
                <button 
                  className="px-3 py-1.5 rounded text-white text-xs font-medium shrink-0"
                  style={{ backgroundColor: template.styles.theme === 'minimal' ? '#6b7280' : themeColors.accent }}
                >
                  اشترك
                </button>
                
                {/* Spacer to push content to the right */}
                <div className="flex-1" />
                
                {/* Gray lines next to image on RIGHT */}
                <div className="text-right">
                  <div className="h-2.5 rounded mb-1.5 ml-auto" style={{ backgroundColor: '#d1d5db', width: '100px' }} />
                  <div className="h-2 rounded ml-auto" style={{ backgroundColor: '#e5e7eb', width: '80px' }} />
                </div>
                
                {/* Image on RIGHT side of screen */}
                <div className="w-10 h-10 rounded-md shrink-0" style={{ backgroundColor: template.styles.theme === 'minimal' ? '#f3f4f6' : `${themeColors.accent}10` }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="p-4" style={getCardStyle()}>
                {/* Image placeholder for cards-with-image */}
                {template.styles.productsLayout === 'cards-with-image' && (
                  <div 
                    className="w-full h-24 rounded-lg mb-3 flex items-center justify-center"
                    style={{ backgroundColor: `${themeColors.accent}15` }}
                  >
                    <Package className="w-8 h-8 opacity-30" style={{ color: themeColors.accent }} />
                  </div>
                )}
                {template.styles.productsLayout !== 'cards-with-image' && (
                  <div 
                    className="w-full h-16 rounded-lg mb-3"
                    style={{ backgroundColor: `${themeColors.accent}15` }}
                  />
                )}
                <div className={`h-3 rounded mb-2 ${isDark ? 'bg-gray-600' : 'bg-gray-200'}`} style={{ width: '70%' }} />
                <div className={`h-2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`} style={{ width: '100%' }} />
                <button 
                  className="w-full mt-3 py-2 rounded-lg text-white text-sm font-medium"
                  style={{ backgroundColor: themeColors.accent }}
                >
                  اشترك الآن
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClinicTemplateSelector;
